import {createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {FONT_FAMILY_PROXIMA, LEGACY_FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {GRAY, GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface GroupCollapserProps {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

interface GroupTitleBarProps extends GroupCollapserProps {
  icon?: BenchlingIconName | null;
  rightHeaderControls?: JSX.Element | null;
  onTitleContextMenu?: React.MouseEventHandler<HTMLDivElement>;

  // The header of the group may be combined of `groupTypeLabel` and `groupName`. If
  // both are present, `groupTypeLabel` would represent the type of the group, such as `Mixture Prep`
  // and `groupName` would be the unique name of the group.
  groupTypeLabel?: string;
  groupName: string | JSX.Element;
  viewer?: boolean;
  // Wraps a long group name onto multiple lines within the title bar instead of keeping it on one line.
  wrapGroupName?: boolean;
}

function GroupCollapser(props: GroupCollapserProps): JSX.Element {
  const text = props.isCollapsed ? 'Expand group' : 'Collapse group';
  return (
    <div
      className={classNames(
        css(styles.collapserChevronWrapper),
        'notebook-export--nlg-collapser-chevron-wrapper'
      )}
      {...getTestProps({component: 'GroupCollapser', element: 'root'})}
    >
      <TooltipWrapper tooltipTitle={text}>
        <BenchlingIcon
          className={classNames(css(styles.collapserIcon), 'notebook-export--nlg-collapser-icon')}
          icon={props.isCollapsed ? 'chevron-down' : 'chevron-up'}
          altText={text}
          onClick={props.onToggleCollapsed}
        />
      </TooltipWrapper>
    </div>
  );
}

export default function GroupTitleBar(props: GroupTitleBarProps): JSX.Element {
  // Print view (i.e. viewer === true) should render the title bar as expanded.
  const isCollapsed = props.viewer ? false : props.isCollapsed;
  const {classes} = useWrapStyles();

  return (
    <div
      className={classNames(
        css(styles.titleBar, !props.wrapGroupName && styles.titleBarSingleLine),
        props.wrapGroupName && classes.titleBarWrapped,
        'notebook-export--nlg-title-bar'
      )}
      {...getTestProps({component: 'GroupTitleBar', element: 'root'})}
    >
      <div
        className={classNames('u-flex-grow', props.wrapGroupName && classes.shrinkable)}
        onContextMenu={props.onTitleContextMenu}
        {...getTestProps({component: 'GroupTitleBar', element: 'titleArea'})}
      >
        <div className={classNames('u-flex-display', props.wrapGroupName && classes.shrinkable)}>
          {props.icon && <BenchlingIcon className="mediocre-tableEditable-tableIcon" icon={props.icon} />}
          {props.groupTypeLabel}
          {props.groupName && (
            <div
              className={classNames(
                classNames(
                  css(styles.groupName, !props.wrapGroupName && styles.groupNameSingleLine),
                  props.wrapGroupName && classes.groupNameWrapped,
                  'notebook-export--nlg-group-name'
                ),
                // Only style this in grey if there is a group label
                props.groupTypeLabel != null &&
                  classNames(css(styles.groupNameSubtitle), 'notebook-export--nlg-group-name-subtitle')
              )}
              {...getTestProps({component: 'GroupTitleBar', element: 'groupName'})}
            >
              {props.groupName}
            </div>
          )}
        </div>
      </div>
      {!props.viewer && <div className="u-flex-none">{props.rightHeaderControls}</div>}
      <GroupCollapser isCollapsed={isCollapsed} onToggleCollapsed={props.onToggleCollapsed} />
    </div>
  );
}

const styles = StyleSheet.create({
  titleBar: {
    alignItems: 'center',
    borderBottom: `1px solid ${GRAY}`,
    color: NEAR_BLACK,
    display: 'flex',
    fontFamily: FONT_FAMILY_PROXIMA,
    ...LEGACY_FONT_PROXIMA_TITLE,
    padding: '5px 10px',
  },

  // Aphrodite marks every rule `!important`, so properties that `useWrapStyles` sets must live in
  // these single-line styles, which are only applied when the group name doesn't wrap.
  titleBarSingleLine: {
    height: '36px',
    lineHeight: 1,
  },

  groupName: {
    color: NEAR_BLACK,
    overflow: 'hidden',
    padding: '0 10px 0 0',
    textOverflow: 'ellipsis',
    userSelect: 'none',
  },

  groupNameSingleLine: {
    whiteSpace: 'nowrap',
  },

  groupNameSubtitle: {
    color: GRAY_DARKER,
    display: 'flex',
    padding: '0 8px',
  },

  collapserChevronWrapper: {
    position: 'absolute',
    right: '-27px',
  },

  collapserIcon: {
    cursor: 'pointer',
    fill: GRAY,
    height: '16px',
    width: '16px',
  },
});

// The `notebook-export--nlg-*` classes in temporary-aphrodite-styles.less also apply in the app and set
// a fixed height and `white-space: nowrap`; `&&` raises specificity so these rules override them
// regardless of stylesheet order.
const useWrapStyles = createStyles(() => ({
  titleBarWrapped: {
    '&&': {
      height: 'auto',
      lineHeight: 1.3,
      minHeight: 36,
    },
  },
  // Flex items default to `min-width: auto`, which keeps them from shrinking below their content width.
  shrinkable: {
    minWidth: 0,
  },
  groupNameWrapped: {
    '&&': {
      overflowWrap: 'anywhere',
      whiteSpace: 'normal',
    },
  },
}));
